/**
 * Collection Grid Custom - Mobile and Desktop Column Switcher
 */
class CollectionGridController {
  constructor() {
    this.gridContainer = document.querySelector('.collection-products-grid');
    this.mobileControlsContainer = document.querySelector(
      '.facets-controls--mobile',
    );
    this.desktopControlsContainer = document.querySelector(
      '.facets-controls--desktop',
    );
    this.gridButtons = document.querySelectorAll(
      '.grid-layout-selector__button',
    );

    // Track each breakpoint's column choice independently
    // Restore from localStorage if available, otherwise use defaults
    this.mobileColumns = this.loadPreference('collection-grid-mobile', 2);
    this.desktopColumns = this.loadPreference('collection-grid-desktop', 4);
    this.isDesktop = window.innerWidth >= 990;

    this.init();
  }

  init() {
    if (!this.gridContainer) return;
    if (!this.mobileControlsContainer && !this.desktopControlsContainer) return;

    this.setupEventListeners();
    this.setInitialState();
  }

  setupEventListeners() {
    this.gridButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const columns = parseInt(button.dataset.columns);
        this.switchGrid(columns);
      });
    });

    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.handleResize();
      }, 250);
    });
  }

  setInitialState() {
    const mobileDefaultButton = document.querySelector(
      `.facets-controls--mobile .grid-layout-selector__button[data-columns="${this.mobileColumns}"]`,
    );
    const desktopDefaultButton = document.querySelector(
      `.facets-controls--desktop .grid-layout-selector__button[data-columns="${this.desktopColumns}"]`,
    );

    if (mobileDefaultButton) {
      this.updateActiveButton(mobileDefaultButton);
    }

    if (desktopDefaultButton) {
      this.updateActiveButton(desktopDefaultButton);
    }

    this.applyGridClass();
  }

  switchGrid(columns) {
    const activeColumns = this.isDesktop ? this.desktopColumns : this.mobileColumns;
    if (columns === activeColumns) return;

    // Store for the current breakpoint
    if (this.isDesktop) {
      this.desktopColumns = columns;
    } else {
      this.mobileColumns = columns;
    }

    this.applyGridClass();

    // Update active icon in the visible control bar
    const selector = this.isDesktop
      ? '.facets-controls--desktop'
      : '.facets-controls--mobile';
    const button = document.querySelector(
      `${selector} [data-columns="${columns}"]`,
    );
    if (button) {
      this.updateActiveButton(button);
    }

    this.storeUserPreference();
  }

  applyGridClass() {
    if (!this.gridContainer) return;

    this.gridContainer.classList.remove(
      'collection-grid--mobile-1-col',
      'collection-grid--mobile-2-col',
      'collection-grid--mobile-3-col',
      'collection-grid--desktop-1-col',
      'collection-grid--desktop-2-col',
      'collection-grid--desktop-3-col',
      'collection-grid--desktop-4-col',
      'collection-grid--desktop-5-col',
      'collection-grid--desktop-6-col',
    );

    if (this.isDesktop) {
      this.gridContainer.classList.add(
        `collection-grid--desktop-${this.desktopColumns}-col`,
      );
    } else {
      this.gridContainer.classList.add(
        `collection-grid--mobile-${this.mobileColumns}-col`,
      );
    }
  }

  handleResize() {
    const wasDesktop = this.isDesktop;
    this.isDesktop = window.innerWidth >= 990;

    // Always re-apply (handles intermediate sizes), but only sync icons on actual breakpoint cross
    this.applyGridClass();

    if (wasDesktop !== this.isDesktop) {
      this.syncActiveButtons();
    }
  }

  syncActiveButtons() {
    // After a breakpoint change, highlight the correct icon in the now-visible control bar
    if (this.isDesktop) {
      const btn = document.querySelector(
        `.facets-controls--desktop [data-columns="${this.desktopColumns}"]`,
      );
      if (btn) this.updateActiveButton(btn);
    } else {
      const btn = document.querySelector(
        `.facets-controls--mobile [data-columns="${this.mobileColumns}"]`,
      );
      if (btn) this.updateActiveButton(btn);
    }
  }

  updateActiveButton(activeButton) {
    if (!activeButton) return;

    const container = activeButton.closest(
      '.facets-controls--mobile, .facets-controls--desktop',
    );
    const containerButtons = container
      ? container.querySelectorAll('.grid-layout-selector__button')
      : [];

    containerButtons.forEach((button) => {
      button.classList.remove('grid-layout-selector__button--active');
      const rects = button.querySelectorAll('svg rect');
      rects.forEach((rect) => {
        rect.style.fill = 'rgba(var(--color-foreground), 0.2)';
      });
    });

    activeButton.classList.add('grid-layout-selector__button--active');

    const activeRects = activeButton.querySelectorAll('svg rect');
    activeRects.forEach((rect) => {
      rect.style.fill = 'rgb(var(--color-button))';
    });
  }

  loadPreference(key, fallback) {
    try {
      const stored = localStorage.getItem(key);
      if (stored !== null) {
        const val = parseInt(stored, 10);
        return val >= 1 && val <= 6 ? val : fallback;
      }
    } catch (e) {
      // Storage unavailable
    }
    return fallback;
  }

  storeUserPreference() {
    try {
      localStorage.setItem('collection-grid-mobile', this.mobileColumns.toString());
      localStorage.setItem('collection-grid-desktop', this.desktopColumns.toString());
    } catch (e) {
      // Storage unavailable
    }
  }

  setColumns(columns) {
    if (columns >= 1 && columns <= 6) {
      this.switchGrid(columns);
    }
  }

  getCurrentColumns() {
    return this.isDesktop ? this.desktopColumns : this.mobileColumns;
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.collectionGrid = new CollectionGridController();
});

// Also handle dynamic content loading (for AJAX)
document.addEventListener('shopify:section:load', () => {
  if (window.collectionGrid) {
    window.collectionGrid = new CollectionGridController();
  }
});
